/**
 * Doc-Match Filename Plausibility Gate — shared between
 *   - DMS-1 (`docApplicability.ts`) — chip-render Tier-3 suppression.
 *   - DMS-4 (`relevanceScoring.ts`) — DM2 scorer short-circuit.
 *
 * Lives in its own tiny module (no `REPORT_TYPE_REGISTRY` or
 * checklist-registry imports) so the DM2 scoring path can use it
 * without dragging the static report-type registry into the
 * `AdminPanel → DocMatchingQualityPanel → adminTelemetry →
 * relevanceScoring` chain. Several AdminPanel tests partially-mock
 * `config/reportTypes`; co-locating the gate here keeps that contract
 * stable.
 *
 * Pure: same inputs → same output. Tested directly in
 * `docMatchFilenameGate.test.ts`.
 */

/**
 * DMS-1 — filename negative patterns keyed on requirement id.
 *
 * The Redhead live-prod regression was a `Data Center Scoring Sheet
 * (Redhead).xlsx` — a finance/economics workbook — surfacing 90%
 * SOLVES chips for Flood / Geotech / Title / Zoning / Utility-locate.
 * Its tags were `cost, capacity, fiber`; nothing about it suggested a
 * borehole log or an FEMA elevation cert.
 *
 * The pattern set below names file naming conventions that DEFINITELY
 * belong to a different doc kind. A match means the doc is suppressed
 * for that requirement before any score-based row is emitted — no
 * Firestore read, no Gemini call.
 *
 * Keep the patterns conservative — false positives here cost real
 * chips. We only block when the filename names a clearly-different
 * doc kind ("scoring sheet", "cost model", "tariff", "PPA", etc.) AND
 * the requirement is one the symptom report flagged. Requirements
 * not listed here keep their legacy behavior.
 */
const REQUIREMENT_FILENAME_NEGATIVE_REGEXES: Record<string, RegExp> = {
  // Physical-evidence requirements — a scoring sheet / cost model /
  // financial workbook is NEVER a borehole log, elevation cert, or
  // title survey.
  'geotech': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|grid[\s_-]*model|fiber[\s_-]*plan|ppa[\s_-]*term/i,
  'flood-cert': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|grid[\s_-]*model|fiber[\s_-]*plan|ppa[\s_-]*term/i,
  'parcel-survey': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|grid[\s_-]*model|fiber[\s_-]*plan|ppa[\s_-]*term/i,
  'survey': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|ppa[\s_-]*term/i,
  'alta-survey': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|grid[\s_-]*model|fiber[\s_-]*plan|ppa[\s_-]*term/i,
  'mineral-title': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|grid[\s_-]*model|fiber[\s_-]*plan|ppa[\s_-]*term/i,
  'utility-locate': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|ppa[\s_-]*term/i,
  // Environmental-survey requirements — a finance/grid workbook is
  // not a Phase I ESA, wetland delineation, or species survey.
  'phase1-esa': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|grid[\s_-]*model|fiber[\s_-]*plan|ppa[\s_-]*term/i,
  'wetland': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|ppa[\s_-]*term/i,
  'species-survey': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|ppa[\s_-]*term/i,
  'cultural': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|ppa[\s_-]*term/i,
  'archaeological-survey': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|grid[\s_-]*model|fiber[\s_-]*plan|ppa[\s_-]*term/i,
  // Zoning verification — same set; a cost model is not a zoning letter.
  'zoning': /scoring[\s_-]*sheet|cost[\s_-]*model|finance|p&l|cashflow|tariff|ppa[\s_-]*term/i,
};

/**
 * Cheap synchronous filter — returns true when the filename clearly
 * names a different doc kind from the requirement. Drives Tier 3
 * suppression (`docApplicability.ts`) and the DM2 Gemini
 * short-circuit (`relevanceScoring.ts` DMS-4). Pure: same input →
 * same output.
 *
 * Returns false for unknown requirement ids (no negative regex
 * registered) — those keep their legacy behavior.
 */
export function filenameStronglyNegates(
  fileName: string | undefined | null,
  requirementId: string,
): boolean {
  if (!fileName) return false;
  const re = REQUIREMENT_FILENAME_NEGATIVE_REGEXES[requirementId];
  if (!re) return false;
  return re.test(fileName);
}
